/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-branding-strategy.js — Branding strategy step (Phase 2.5)
 *
 * Bundle PDP's step 1 per `mockups/bundle-step1-branding-strategy-*.html`.
 * Multistep PDP does NOT use this step — it enters at `intro` instead.
 *
 * Two-choice card pattern, shared visually with `logo_strategy` (the
 * .jrs-card-choice CSS class family in jrs-v2-steps.css covers both
 * controllers). Each card has:
 *   - Optional "Recommended for most" badge (top)
 *   - Visual radio circle (right side)
 *   - Title (h2)
 *   - Description paragraph
 *   - Meta row at the bottom (clock icon + time estimate + separator + detail)
 *
 * State path per Doc 6 §5.3:
 *   state.draft.branding_strategy = 'bundle_wide' | 'per_product'
 *
 * Telemetry:
 *   pdp_customizer_decision per Doc 3 v0.2 §4.5 with
 *   decision_type='branding_strategy', decision_value=<choice>
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'branding_strategy';

  var _mountEl = null;
  var _listeners = [];

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopup.setReassurance(
      'Free design proof on every order. Change your mind any time.'
    );
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    // Headwear (or otherwise forced) bundles can't share one logo setup across
    // product types, so the same-vs-different choice is skipped: render an intro
    // that sets up the per-product flow, then continue.
    if (_isForcedPerProduct(state)) {
      _renderIntro();
      return;
    }

    var current = (state.draft && state.draft.branding_strategy) || null;
    // Preselect bundle_wide so the step opens resolved rather than blank
    // (standing decision: the first option is always selected). Skipped whenever
    // the customer already has a choice — restored from saved state
    // (context.isResume), reached via Back (context.direction 'back'), cart-edit,
    // or any existing branding_strategy in the draft. A default is not a user
    // decision, so this fires no telemetry; the decision event fires on the CTA.
    if (_shouldPreselect(state, context, current)) {
      current = 'bundle_wide';
      _setChoice(current);
    }
    var bundleSize = _resolveBundleSize(context);
    _render(current, bundleSize);
    _bindInteractions();
    _syncCta(true);
  }

  function _shouldPreselect(state, context, current) {
    if (current === 'bundle_wide' || current === 'per_product') return false;
    if (context && context.isResume) return false;
    if (context && context.direction === 'back') return false;
    if (state && state.editMode && state.editMode.active) return false;
    if (state && state.flow === 'cart-edit') return false;
    return true;
  }

  function _isForcedPerProduct(state) {
    if (state && state.draft && state.draft._force_per_product) return true;
    return !!(JRS.FlowBundle && typeof JRS.FlowBundle.isHeadwearBundle === 'function' &&
              JRS.FlowBundle.isHeadwearBundle());
  }

  function _activeCategoryNames() {
    var State = JRS.CustomizerPopupState;
    var list = (State && typeof State.getActiveCategoryList === 'function') ? State.getActiveCategoryList() : [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].name) out.push(list[i].name);
    }
    return out;
  }

  function _mergeDraft(fields) {
    var State = JRS.CustomizerPopupState;
    var cur = State.getState().draft || {};
    var next = {};
    var k;
    for (k in cur) { if (Object.prototype.hasOwnProperty.call(cur, k)) next[k] = cur[k]; }
    for (k in fields) { if (Object.prototype.hasOwnProperty.call(fields, k)) next[k] = fields[k]; }
    State.dispatch({ type: 'SET_DRAFT', payload: { draft: next } });
  }

  function _renderIntro() {
    // Lock the mode in (covers the isHeadwearBundle-detected path where the
    // resolveEntry flag wasn't set).
    _mergeDraft({ branding_strategy: 'per_product', _force_per_product: true });

    var names = _activeCategoryNames();
    var itemsHtml = '';
    for (var i = 0; i < names.length; i++) {
      itemsHtml +=
        '<li class="jrs-pp-intro__item">' +
        '  <span class="jrs-pp-intro__num" aria-hidden="true">' + (i + 1) + '</span>' +
        '  <span class="jrs-pp-intro__name">' + _escape(names[i]) + '</span>' +
        '</li>';
    }

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--pp-intro" data-jrs-step="branding_strategy">' +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title">Set up each product</h2>' +
      '    <p class="jrs-step__subtitle">Your bundle mixes product types with different logo areas, so you\'ll set branding for each one in turn.</p>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
      (itemsHtml ? '<ol class="jrs-pp-intro__list">' + itemsHtml + '</ol>' : '') +
      '  </section>' +
      '</div>';

    // Name the first product the customer will set up. `names` is the same
    // array the numbered list above renders, so the button always matches row 1;
    // it comes from getActiveCategoryList(), the list FlowBundle walks in order.
    var pop = JRS.CustomizerPopup;
    if (pop && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({
        label: names.length ? 'Start with ' + names[0] : 'Start customising',
        onClick: _onIntroContinue
      });
    }
  }

  function _onIntroContinue(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    _mergeDraft({ branding_strategy: 'per_product' });
    JRS.CustomizerPopup.goToStep('position_picker', { trigger: 'primary_cta' });
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var s = state.draft && state.draft.branding_strategy;
    if (s !== 'bundle_wide' && s !== 'per_product') {
      return { valid: false, errors: { branding_strategy: 'required' } };
    }
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _resolveBundleSize(context) {
    // Best-effort: count bundle_children if the PDP trigger populated it.
    // Falls back to the mockup's example "6-item" wording so the subtitle
    // doesn't read awkwardly.
    if (context && context.bundle_children && context.bundle_children.length > 0) {
      return context.bundle_children.length;
    }
    return null;
  }

  function _render(current, bundleSize) {
    var perItemTime = bundleSize
      ? '~3 minutes × ' + bundleSize + ' items'
      : '~3 minutes per item';

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--branding-strategy" data-jrs-step="branding_strategy">' +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title">Same logo across all items?</h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
      '    <div class="jrs-card-choices" role="radiogroup" aria-label="Branding strategy">' +
           _renderCard({
             value: 'bundle_wide',
             badge: 'Recommended',
             title: 'Same logo',
             desc: 'One logo on your whole bundle. Best for team kit.',
             metaTime: '~2 minutes',
             selected: current === 'bundle_wide'
           }) +
           _renderCard({
             value: 'per_product',
             badge: null,
             title: 'Different logo per product',
             desc: 'A different logo, position or method on each item.',
             metaTime: perItemTime,
             selected: current === 'per_product'
           }) +
      '    </div>' +
      '  </section>' +
      '</div>';
  }

  function _renderCard(opts) {
    var badgeHtml = opts.badge
      ? '<span class="jrs-card-choice__badge">' + _escape(opts.badge) + '</span>'
      : '';
    return (
      '<button type="button"' +
      '        class="jrs-card-choice' + (opts.selected ? ' jrs-card-choice--selected' : '') + '"' +
      '        role="radio"' +
      '        aria-checked="' + (opts.selected ? 'true' : 'false') + '"' +
      '        data-jrs-action="strategy-choice"' +
      '        data-jrs-value="' + _escape(opts.value) + '"' +
      '        tabindex="' + (opts.selected ? '0' : '-1') + '">' +
        badgeHtml +
      '  <span class="jrs-card-choice__radio" aria-hidden="true"></span>' +
      '  <h3 class="jrs-card-choice__title">' + _escape(opts.title) + '</h3>' +
      '  <p class="jrs-card-choice__desc">' + _escape(opts.desc) + '</p>' +
      '  <div class="jrs-card-choice__meta">' +
      '    <svg class="jrs-card-choice__meta-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '      <circle cx="8" cy="8" r="6.5"/>' +
      '      <path d="M8 4.5 L8 8 L10.5 9.5"/>' +
      '    </svg>' +
      '    <span class="jrs-card-choice__meta-time">' + _escape(opts.metaTime) + '</span>' +
      '  </div>' +
      '</button>'
    );
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    var buttons = _mountEl.querySelectorAll('[data-jrs-action="strategy-choice"]');
    for (var i = 0; i < buttons.length; i++) {
      _bind(buttons[i], 'click', _onChoiceClick);
    }
  }

  // Selecting a card records the choice and marks the card; it no longer
  // advances. An option is always selected now (bundle_wide preselected on
  // mount), so moving on is a deliberate press of the chrome-footer CTA.
  function _onChoiceClick(e) {
    var value = e.currentTarget && e.currentTarget.getAttribute('data-jrs-value');
    if (value !== 'bundle_wide' && value !== 'per_product') return;
    _setChoice(value);
    _markSelected(value);
    _syncCta(false);
  }

  // Patch the two cards in place rather than re-rendering, so the pressed card
  // keeps focus. Mirrors the markup _renderCard emits.
  function _markSelected(value) {
    if (!_mountEl) return;
    var buttons = _mountEl.querySelectorAll('[data-jrs-action="strategy-choice"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var on = btn.getAttribute('data-jrs-value') === value;
      btn.classList.toggle('jrs-card-choice--selected', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    }
  }

  // CTA label names the first screen the choice leads to. bundle_wide goes to
  // the shared position_picker. per_product starts the per-category loop at
  // getActiveCategoryList()[0] — the same list, in the same order, that
  // FlowBundle.completeCategoryOrAdvance walks (cursor 0 first) — so its name is
  // the product the customer actually lands on. No name → the generic label.
  function _ctaLabel(strategy) {
    if (strategy === 'per_product') {
      var names = _activeCategoryNames();
      if (names.length) return 'Start with ' + names[0];
    }
    return 'Choose your positions';
  }

  // Chrome-footer CTA. Enabled whenever a strategy is selected, which on every
  // normal entry is always (fresh mounts preselect; restores carry a choice).
  // The disabled branch covers only the edge where a restored / Back / cart-edit
  // draft arrives with NO strategy and preselect is deliberately skipped: an
  // enabled button there would press into onValidate and silently do nothing.
  // Same shape as position_picker._syncCta: setPrimaryCta on mount,
  // updatePrimaryCta after, so the label follows the selected card.
  function _syncCta(initial) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    var draft = (JRS.CustomizerPopupState.getState() || {}).draft || {};
    var strategy = draft.branding_strategy;
    var hasChoice = strategy === 'bundle_wide' || strategy === 'per_product';
    var label = _ctaLabel(strategy);
    if (initial && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({ label: label, disabled: !hasChoice, onClick: _onContinue });
    } else if (typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ label: label, disabled: !hasChoice });
    }
  }

  // Commit + advance. _applyChoice re-writes the (already set) fields — a no-op
  // for bundle_wide, and for per_product it resets the category cursor to 0
  // exactly as the old tap did right before advancing — and fires the decision
  // event, which therefore now fires on this press rather than on the card tap.
  function _onContinue() {
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;
    _applyChoice(state.draft.branding_strategy);
    JRS.CustomizerPopup.goToStep('position_picker', { trigger: 'primary_cta' });
  }

  // The state half of _applyChoice, without telemetry — used by the card tap
  // and the mount-time preselect, neither of which is a committed decision.
  function _setChoice(value) {
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'branding_strategy', value: value }
    });
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: '_current_category_index', value: value === 'per_product' ? 0 : null }
    });
  }

  /**
   * Write branding_strategy + initialize/clear the per_product loop cursor
   * (spec D4), then fire decision telemetry. Extracted as a test seam +
   * shared logic. per_product → _current_category_index = 0 (start the loop at
   * the first category); bundle_wide → null (clear any lingering cursor from a
   * prior per_product attempt so the flow/review read it as unset).
   * Belt-and-suspenders alongside FlowBundle.completeCategoryOrAdvance's
   * default-to-0; the explicit init makes the cursor observable during the
   * first category's steps (needed for S2's progress pill).
   * @param {'bundle_wide'|'per_product'} value
   */
  function _applyChoice(value) {
    _setChoice(value);
    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'branding_strategy',
      decision_value: value
    });
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.BrandingStrategy = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    _applyChoice: _applyChoice, // test seam (Phase per_product S1)
    _isForcedPerProduct: _isForcedPerProduct, // test seam (headwear force per-product)
    _onIntroContinue: _onIntroContinue // test seam (headwear intro Continue)
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
